<template>
  <div v-if="canView" class="bg-white border border-gray-200 rounded-lg">
    <div class="border-b border-gray-200">
      <div class="flex items-center justify-between px-6 py-3">
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold text-gray-900">Performance Signals</span>
          <span class="text-[10px] text-gray-400 tabular-nums">{{ filtered.length }} constraints</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="hasFilters"
            class="text-[10px] text-gray-400 hover:text-gray-600"
            @click="clearFilters"
          >Clear filters</button>
          <ExportMenu
size="sm" :disabled="loading || !filtered.length" :exports="[
            { type: 'csv', action: exportCsv },
            { type: 'excel', action: exportXlsx },
          ]" />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2 px-6 pb-3">
        <select v-model="filterPractice" class="sensitive text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 max-w-[16rem]">
          <option :value="null">All practices</option>
          <option v-for="n in practiceOptions" :key="n" :value="n">{{ titleCase(n) }}</option>
        </select>
        <select v-model="filterOwner" class="sensitive text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 max-w-[14rem]">
          <option :value="null">All owners</option>
          <option :value="PRACTICE_OWNED">Practice-owned</option>
          <option :value="NO_OWNER">No owner</option>
          <option v-for="o in ownerOptions" :key="o.id" :value="o.id">{{ o.name }}</option>
        </select>
        <select v-model="filterInitiative" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All initiatives</option>
          <option v-for="k in initiativeOptions" :key="k" :value="k">{{ humanize(k) }}</option>
        </select>
        <select v-model="filterGap" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All gap types</option>
          <option v-for="g in GAP_TYPES" :key="g" :value="g">{{ humanize(g) }}</option>
        </select>
        <select v-model="filterStatus" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <input
          v-model="search" data-demo-search
          type="text"
          placeholder="Search..."
          class="sensitive text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 w-48 ml-auto"
        >
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <LoadingIndicator size="md" />
    </div>
    <div v-else-if="error" class="px-6 py-12 text-center text-xs text-red-500">{{ error }}</div>
    <div v-else-if="!rows.length" class="px-6 py-12 text-center text-xs text-gray-400">
      No constraints in the graph yet.
    </div>

    <!-- What resolving constraints is worth, per initiative: the live peer event study
         over resolved action items. Sits above the operational view so the table is read
         against the effect it is meant to produce. -->
    <div v-else class="px-6 py-4 border-b border-gray-200">
      <div class="flex items-start justify-between gap-6">
        <div class="grid grid-cols-2 gap-4 flex-1">
          <div v-if="impactLoading" class="col-span-2 text-[11px] text-gray-400">Measuring effect of resolved actions…</div>
          <div
            v-for="e in impact"
            v-else
            :key="e.initiative_key"
            class="border border-gray-200 rounded-lg px-4 py-3"
          >
            <div class="flex items-baseline gap-2 mb-1">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="initiativeCls(e.initiative_key)"
              >{{ humanize(e.initiative_key) }}</span>
              <template v-if="e.did_per_100_per_year != null">
                <span class="text-sm font-semibold tabular-nums" :class="e.did_per_100_per_year >= 0 ? 'text-gray-900' : 'text-red-700'">
                  {{ e.did_per_100_per_year >= 0 ? '+' : '' }}{{ e.did_per_100_per_year }}
                </span>
                <span class="text-[10px] text-gray-400">{{ e.unit_label }}</span>
                <span
                  class="text-[10px] ml-auto tabular-nums"
                  :class="e.significant ? 'text-green-700' : 'text-amber-700'"
                >p={{ e.did_p_value.toFixed(3) }}</span>
              </template>
              <span v-else class="text-[10px] text-gray-400 ml-auto">not estimable yet</span>
            </div>
            <div class="text-[11px] text-gray-600 leading-snug">
              <template v-if="e.did_per_100_per_year != null">
                {{ e.n_events }} resolved actions across {{ e.n_practices }} practices;
                {{ e.did_pct_positive }}% moved the metric ahead of peers.
              </template>
              <template v-else>
                {{ e.n_resolved_actions }} resolved actions, not enough with a usable metric window on both sides.
              </template>
            </div>
            <div v-if="e.low_confidence" class="text-[10px] text-amber-700 mt-1.5">{{ e.caveat }}</div>
          </div>
        </div>
        <button
          class="text-[10px] text-gray-400 hover:text-gray-600 shrink-0 mt-1"
          @click="showMethod = !showMethod"
        >{{ showMethod ? 'Hide' : 'Method' }}</button>
      </div>

      <div v-if="showMethod" class="mt-3 text-[11px] text-gray-600 leading-relaxed space-y-2 border-t border-gray-100 pt-3">
        <p>
          The Engagement Tracker reads every practice meeting and records each constraint:
          a durable obstacle to value-based work, typed by <strong>why</strong> the work is
          not happening. Knowledge, capacity, ownership and motivation are judged for whoever
          in the network must act; patient engagement and third party name obstacles the
          network cannot remove. An action item that addresses a constraint is the treatment.
        </p>
        <p>
          <strong>Effect</strong> is a peer difference-in-differences: for every resolved
          action, the practice's metric movement in the window after resolution against the
          window before, minus the same movement across every other practice over the
          identical calendar dates. AWV completion and visit coverage are read as velocity
          per 100 patients per year; transitions of care as pooled follow-up compliance
          because per-week rates on a dozen discharges are noise; risk documentation as the
          fraction of the gap to target closed, and is low confidence because recapture is
          cumulative within the year. Contract payments has no metric to score.
        </p>
        <p class="text-gray-400">
          Estimates are associational: actions are not randomly assigned, and practices that
          get constraints closed may differ from those that do not.
        </p>
      </div>
    </div>

    <!-- When constraints were raised and when they were resolved. Bars are the monthly
         counts; the line is the running count of addressable constraints still open.
         Respects the filters, unlike the owner summary below. -->
    <div v-if="!loading && !error && trend.length" class="px-6 pt-4 pb-2 border-b border-gray-200">
      <div class="flex items-baseline gap-4">
        <span class="text-[10px] text-gray-400 uppercase tracking-wider">Raised and resolved over time</span>
        <span class="text-[10px] text-gray-400 ml-auto tabular-nums">
          {{ trendTotals.raised }} raised
          <span class="text-gray-500">({{ trendTotals.blocked }} patient or third party)</span>
          · {{ trendTotals.resolved }} resolved · {{ trendTotals.open }} still open
        </span>
      </div>
      <Chart_V5
        :chart_data="trendChart"
        :chart_config="trendConfig"
        override-height="h-[260px]"
      />
    </div>

    <!-- Closure by field-team owner. Bar is resolved / (resolved + open); patient and
         third-party constraints are excluded since nobody on the team can close them. -->
    <div v-if="!loading && !error && ownerSummary.length" class="px-6 py-3 border-b border-gray-200 bg-gray-50">
      <div class="grid grid-cols-3 gap-x-6 gap-y-2">
        <button
          v-for="r in ownerSummary"
          :key="r.id"
          class="text-left group"
          :class="filterOwner === r.id ? 'opacity-100' : 'opacity-80 hover:opacity-100'"
          @click="filterOwner = filterOwner === r.id ? null : r.id"
        >
          <div class="flex items-baseline gap-2">
            <span
              class="sensitive text-xs truncate"
              :class="filterOwner === r.id ? 'font-semibold text-gray-900' : 'text-gray-700'"
            >{{ r.name }}</span>
            <span class="text-[10px] text-gray-400 truncate">{{ r.role }}</span>
            <span class="text-[10px] text-gray-400 tabular-nums ml-auto shrink-0">
              {{ r.closed }}/{{ r.closed + r.open }}
            </span>
            <span class="text-xs font-semibold text-gray-900 tabular-nums shrink-0 w-9 text-right">
              {{ r.rate }}%
            </span>
          </div>
          <div class="flex h-1.5 rounded-sm overflow-hidden bg-gray-200 mt-1">
            <div class="bg-green-500" :style="{ width: `${r.rate}%` }"></div>
          </div>
        </button>
      </div>
      <div class="text-[10px] text-gray-400 mt-2">
        Resolved / addressable constraints owned by each team member. Excludes patient and third-party constraints.
      </div>
    </div>

    <div v-if="!loading && !error && rows.length" class="overflow-auto" style="max-height: 700px;">
      <table class="w-full text-xs">
        <thead class="sticky top-0 bg-gray-50 z-10">
          <tr class="text-gray-400 text-[10px] uppercase tracking-wider border-b border-gray-200">
            <th
              v-for="c in columns"
              :key="c.key"
              class="font-medium px-3 py-2 cursor-pointer hover:text-gray-600 whitespace-nowrap"
              :class="c.align === 'right' ? 'text-right' : 'text-left'"
              @click="sortBy(c.key)"
            >
              {{ c.label }}
              <span v-if="sortKey === c.key" class="text-gray-600">{{ sortDesc ? '↓' : '↑' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="g in filtered"
            :key="g.id"
            class="border-b border-gray-100 cursor-pointer"
            :class="selected && selected.id === g.id ? 'bg-blue-50' : 'hover:bg-gray-50'"
            @click="openDrawer(g)"
          >
            <td class="sensitive px-3 py-1.5 text-gray-900">{{ titleCase(g.practice) }}</td>
            <td class="sensitive px-3 py-1.5 text-gray-700">
              <span class="sensitive" data-spoof-kind="person">{{ g.owner || '—' }}</span> <span v-if="g.owner_role" class="text-gray-400">· <span class="sensitive">{{ g.owner_role }}</span></span>
            </td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                :class="initiativeCls(g.initiative_key)"
              >{{ humanize(g.initiative_key) }}</span>
            </td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                :class="gapCls(g.gap_type)"
              >{{ humanize(g.gap_type) }}</span>
            </td>
            <td class="sensitive px-3 py-1.5 text-gray-700" data-demo-blur>{{ g.title }}</td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                :class="STATUS_CLS[g.status] || STATUS_CLS.open"
              >{{ humanize(g.status) }}</span>
            </td>
            <td class="px-3 py-1.5" :class="SEVERITY_CLS[g.severity] || 'text-gray-400'">{{ g.severity || '—' }}</td>
            <td class="px-3 py-1.5 text-gray-400 tabular-nums whitespace-nowrap">{{ g.raised_at || '—' }}</td>
            <td class="px-3 py-1.5 text-gray-400 tabular-nums whitespace-nowrap">{{ g.closed_at || '—' }}</td>
            <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">
              {{ g.actions.length || '—' }}
              <span v-if="g.actions_done" class="text-green-700">· {{ g.actions_done }} done</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detail drawer: the constraint, the actions taken against it, and the verbatim
         quotes behind it, fetched when opened. -->
    <div v-if="selected" class="fixed inset-0 z-50 flex justify-end" @click.self="selected = null">
      <div class="bg-black/25 absolute inset-0" @click="selected = null"></div>
      <div class="relative w-[620px] h-full bg-white shadow-xl overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-start justify-between z-10">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 mb-0.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="initiativeCls(selected.initiative_key)"
              >{{ humanize(selected.initiative_key) }}</span>
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="gapCls(selected.gap_type)"
              >{{ humanize(selected.gap_type) }}</span>
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="STATUS_CLS[selected.status] || STATUS_CLS.open"
              >{{ humanize(selected.status) }}</span>
              <span v-if="selected.severity" class="text-[10px]" :class="SEVERITY_CLS[selected.severity]">{{ selected.severity }}</span>
            </div>
            <DemoText tag="div" class="text-xs font-semibold text-gray-900" :text="selected.title" />
          </div>
          <button class="text-gray-400 hover:text-gray-600 ml-4 shrink-0" @click="selected = null">&times;</button>
        </div>

        <div class="px-5 py-4 space-y-4">
          <DemoText tag="p" v-if="detail && detail.description" class="text-xs text-gray-700 leading-relaxed" :text="detail.description" />
          <p v-else-if="detailLoading" class="text-xs text-gray-400">Loading…</p>

          <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div>
              <div class="text-[10px] text-gray-400 uppercase">Practice</div>
              <router-link :to="trackerLink(selected)" class="sensitive text-blue-600 hover:underline">
                {{ titleCase(selected.practice) }} →
              </router-link>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">Owner</div>
              <div class="sensitive text-gray-900">
                <span class="sensitive" data-spoof-kind="person">{{ selected.owner || '—' }}</span> <span v-if="selected.owner_role" class="text-gray-400">· <span class="sensitive">{{ selected.owner_role }}</span></span>
              </div>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">First raised</div>
              <div class="text-gray-900 tabular-nums">{{ selected.raised_at || '—' }}</div>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">Resolved</div>
              <div class="text-gray-900 tabular-nums">{{ selected.closed_at || '—' }}</div>
            </div>
          </div>

          <div>
            <div class="text-[10px] text-gray-400 uppercase mb-1">Actions addressing it</div>
            <div v-if="!selected.actions.length" class="text-xs text-gray-400">Nothing recorded.</div>
            <div
              v-for="a in selected.actions"
              :key="a.id"
              class="flex items-baseline gap-2 text-xs text-gray-700"
            >
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0"
                :class="STATUS_CLS[a.status] || STATUS_CLS.open"
              >{{ humanize(a.status) }}</span>
              <span class="sensitive" data-demo-blur>{{ a.title }}</span>
            </div>
          </div>

          <div v-if="quotes.length">
            <div class="text-[10px] text-gray-400 uppercase mb-1">Evidence</div>
            <div
              v-for="e in quotes"
              :key="e.id"
              class="text-xs text-gray-600 pl-3 border-l-2 border-gray-200 mb-2"
            >
              "<DemoText tag="span" :text="e.quote" />"
              <span class="block text-[10px] text-gray-400 mt-0.5">
                <span class="sensitive">{{ e.engagement_date }}</span> · <span class="sensitive">{{ e.engagement_subject }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import Chart_V5 from '../widgetcomponents/Viz_Elements/Chart_V5.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import { blockSensitiveEgress } from '@/utils/demoEgress';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import DemoText from '@/components/DemoMode/DemoText.vue';

const PRACTICE_OWNED = '__practice__';
const NO_OWNER = '__none__';

const GAP_TYPES = ['knowledge', 'capacity', 'ownership', 'motivation', 'patient_engagement', 'third_party'];
// The two gap types nobody in the network can remove: shown in the raised bar, kept out
// of the open line and the closure rates.
const BLOCKED = new Set(['patient_engagement', 'third_party']);

const GAP_CLS = {
  knowledge: 'bg-sky-50 text-sky-700',
  capacity: 'bg-orange-50 text-orange-700',
  ownership: 'bg-violet-50 text-violet-700',
  motivation: 'bg-rose-50 text-rose-700',
  patient_engagement: 'bg-emerald-50 text-emerald-700',
  third_party: 'bg-slate-100 text-slate-700',
};
const INITIATIVE_PALETTE = [
  'bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-teal-100 text-teal-700',
  'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-indigo-100 text-indigo-700',
  'bg-lime-100 text-lime-700', 'bg-cyan-100 text-cyan-700',
];
const STATUS_CLS = {
  resolved: 'bg-green-100 text-green-700',
  in_progress: 'bg-amber-100 text-amber-700',
  open: 'bg-red-100 text-red-700',
  dropped: 'bg-gray-100 text-gray-500',
};
const SEVERITY_CLS = { high: 'text-red-700 font-semibold', medium: 'text-amber-700', low: 'text-gray-500' };

const COLUMNS = [
  { key: 'practice', label: 'Practice' },
  { key: 'owner', label: 'Owner' },
  { key: 'initiative_key', label: 'Initiative' },
  { key: 'gap_type', label: 'Gap' },
  { key: 'title', label: 'Constraint' },
  { key: 'status', label: 'Status' },
  { key: 'severity', label: 'Severity' },
  { key: 'raised_at', label: 'Raised' },
  { key: 'closed_at', label: 'Resolved' },
  { key: 'actions_count', label: 'Actions', align: 'right' },
];

export default {
  name: 'PerformanceSignalStudy',
  components: { DemoText, LoadingIndicator, ExportMenu, Chart_V5 },
  mixins: [demoProtectedFields],
  // The search (matches practice and owner names) identifies real practices and staff. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  props: {
    year: { type: Number, default: null },
    entityId: { type: Number, default: null },
    metricId: { type: Number, default: null },
    metricIds: { type: Array, default: () => [] },
    performanceDomainId: { type: Number, default: null },
    isDomain: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      error: null,
      rows: [],
      impact: [],
      impactLoading: true,
      search: '',
      filterInitiative: null,
      filterPractice: null,
      filterOwner: null,
      filterGap: null,
      filterStatus: null,
      selected: null,
      detail: null,
      detailLoading: false,
      showMethod: false,
      sortKey: 'practice',
      sortDesc: false,
      columns: COLUMNS,
      GAP_TYPES,
      STATUS_CLS,
      SEVERITY_CLS,
      PRACTICE_OWNED,
      NO_OWNER,
    };
  },
  computed: {
    canView() {
      return (
        this.$store.getters['auth/current_user']?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        false
      );
    },
    practiceOptions() {
      return [...new Set(this.rows.map(g => g.practice).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    initiativeOptions() {
      return [...new Set(this.rows.map(g => g.initiative_key || 'unassigned'))].sort();
    },
    // The field team: owners who are app users. Practice staff own constraints too but
    // are one option, not a list of four hundred names.
    ownerOptions() {
      const seen = new Map();
      for (const g of this.rows) {
        if (g.owner_is_team && !seen.has(g.owner_id)) seen.set(g.owner_id, { id: g.owner_id, name: g.owner });
      }
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    hasFilters() {
      return !!(this.filterPractice || this.filterOwner || this.filterInitiative
        || this.filterGap || this.filterStatus || this.search);
    },
    // Closure rate per team owner. Ignores the filters on purpose: it is the standing
    // picture of who is closing what, not a view of the filtered set.
    ownerSummary() {
      const by = {};
      for (const g of this.rows) {
        if (!g.owner_is_team || BLOCKED.has(g.gap_type)) continue;
        const r = (by[g.owner_id] = by[g.owner_id] || { id: g.owner_id, name: g.owner, role: g.owner_role, closed: 0, open: 0 });
        if (g.status === 'resolved') r.closed += 1;
        else r.open += 1;
      }
      return Object.values(by)
        .map(r => ({ ...r, rate: r.closed + r.open ? Math.round((100 * r.closed) / (r.closed + r.open)) : 0 }))
        .sort((a, b) => b.rate - a.rate || b.closed + b.open - (a.closed + a.open));
    },
    // Monthly raised / resolved from the FILTERED set, plus the running count of
    // addressable constraints not yet closed.
    trend() {
      const by = {};
      const touch = k => (by[k] = by[k] || { raised: 0, addressable: 0, blocked: 0, resolved: 0, closed: 0 });
      for (const g of this.filtered) {
        const rm = (g.raised_at || '').slice(0, 7);
        const blocked = BLOCKED.has(g.gap_type);
        if (rm) {
          touch(rm).raised += 1;
          if (blocked) by[rm].blocked += 1;
          else by[rm].addressable += 1;
        }
        const sm = (g.closed_at || '').slice(0, 7);
        if (sm) {
          touch(sm).resolved += 1;
          if (!blocked) by[sm].closed += 1;
        }
      }
      const months = Object.keys(by).sort();
      if (!months.length) return [];
      // The axis starts at January of the current year. Anything raised or closed before
      // that still counts toward the opening stock, so the open line starts where it
      // should rather than at zero.
      const out = [];
      const [y1, m1] = months[months.length - 1].split('-').map(Number);
      const y0 = new Date().getFullYear();
      let running = 0;
      for (const k of months) {
        if (k >= `${y0}-01`) break;
        running += by[k].addressable - by[k].closed;
      }
      for (let y = y0, m = 1; y < y1 || (y === y1 && m <= m1); m === 12 ? (m = 1, y += 1) : (m += 1)) {
        const k = `${y}-${String(m).padStart(2, '0')}`;
        const r = by[k] || { raised: 0, addressable: 0, blocked: 0, resolved: 0, closed: 0 };
        running += r.addressable - r.closed;
        out.push({
          month: k,
          raised: r.raised,
          addressable: r.addressable,
          blocked: r.blocked,
          resolved: r.resolved,
          open: running,
          label: new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' }),
        });
      }
      return out;
    },
    trendChart() {
      const t = this.trend;
      return {
        labels: t.map(m => m.label),
        series: [
          {
            values: t.map(m => m.addressable),
            config: {
              label: 'Raised — addressable',
              backgroundColor: '#A5A5A6',
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              tooltip: { formatter: d => this.raisedTooltip(d) },
            },
          },
          {
            values: t.map(m => m.blocked),
            config: {
              label: 'Raised — patient / third party',
              backgroundColor: '#DDDDDD',
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              tooltip: { formatter: d => this.raisedTooltip(d) },
            },
          },
          {
            values: t.map(m => m.resolved),
            config: {
              label: 'Resolved',
              backgroundColor: '#389A6C',
              type: 'bar',
              yAxis: 'left',
              offset: 1,
            },
          },
          {
            values: t.map(m => m.open),
            config: {
              label: 'Still open',
              backgroundColor: '#F76734',
              type: 'line',
              yAxis: 'right',
              offset: 0,
            },
          },
        ],
      };
    },
    trendConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 60, bottom: 50, left: 55 },
        xAxis: { labelRotation: -45, fontSize: 10 },
        yAxes: [
          { position: 'left', min: 0, fontSize: 10 },
          { position: 'right', min: 0, fontSize: 10 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        hideDataLabels: true,
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 10 },
      };
    },
    trendTotals() {
      const sum = k => this.trend.reduce((a, m) => a + (m[k] || 0), 0);
      return {
        raised: sum('raised'),
        blocked: sum('blocked'),
        resolved: sum('resolved'),
        open: this.trend.length ? this.trend[this.trend.length - 1].open : 0,
      };
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let out = this.rows.filter(g => {
        if (this.filterPractice && g.practice !== this.filterPractice) return false;
        if (this.filterInitiative && (g.initiative_key || 'unassigned') !== this.filterInitiative) return false;
        if (this.filterOwner === PRACTICE_OWNED && (!g.owner || g.owner_is_team)) return false;
        if (this.filterOwner === NO_OWNER && g.owner) return false;
        if (this.filterOwner && this.filterOwner !== PRACTICE_OWNED && this.filterOwner !== NO_OWNER && g.owner_id !== this.filterOwner) return false;
        if (this.filterGap && g.gap_type !== this.filterGap) return false;
        if (this.filterStatus && g.status !== this.filterStatus) return false;
        if (!q) return true;
        return [g.practice, g.owner, g.title, g.initiative_key, g.gap_type]
          .some(v => (v || '').toString().toLowerCase().includes(q));
      });
      const k = this.sortKey;
      out = [...out].sort((a, b) => {
        const av = a[k];
        const bv = b[k];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return av - bv;
        return String(av).localeCompare(String(bv));
      });
      return this.sortDesc ? out.reverse() : out;
    },
    quotes() {
      return ((this.detail && this.detail.evidence) || [])
        .filter(e => e.type === 'transcript_quote' && e.data && e.data.quote)
        .map(e => ({ id: e.id, quote: e.data.quote, engagement_date: e.engagement_date, engagement_subject: e.engagement_subject }));
    },
  },
  mounted() {
    if (this.canView) {
      this.fetch();
      this.fetchImpact();
    }
  },
  methods: {
    humanize(k) {
      return (k || 'unassigned').replaceAll('_', ' ');
    },
    gapCls(g) {
      return GAP_CLS[g] || 'bg-gray-100 text-gray-500';
    },
    initiativeCls(k) {
      const i = this.initiativeOptions.indexOf(k || 'unassigned');
      return i === -1 ? 'bg-gray-100 text-gray-600' : INITIATIVE_PALETTE[i % INITIATIVE_PALETTE.length];
    },
    trackerLink(g) {
      return { path: `/entity/${g.practice_id}/engagement-tracker`, query: { node: g.id } };
    },
    async openDrawer(g) {
      this.selected = g;
      this.detail = null;
      this.detailLoading = true;
      try {
        const { data } = await axios.get(`api/engagement-graph-nodes/${g.id}/`);
        if (this.selected && this.selected.id === g.id) this.detail = data;
      } finally {
        this.detailLoading = false;
      }
    },
    titleCase(s) {
      if (!s) return '';
      return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    raisedTooltip(d) {
      const m = this.trend.find(x => x.label === d.label);
      if (!m) return '';
      const pct = m.raised ? Math.round((100 * m.blocked) / m.raised) : 0;
      return `<strong>${m.label}</strong><br/>`
        + `${m.raised} raised<br/>`
        + `${m.addressable} addressable<br/>`
        + `${m.blocked} patient or third party (${pct}%)`;
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortDesc = !this.sortDesc;
      else { this.sortKey = key; this.sortDesc = false; }
    },
    clearFilters() {
      this.filterPractice = null;
      this.filterOwner = null;
      this.filterInitiative = null;
      this.filterGap = null;
      this.filterStatus = null;
      this.search = '';
    },
    exportRows() {
      return this.filtered.map(g => ({
        Practice: this.titleCase(g.practice),
        Owner: g.owner || '',
        'Owner Role': g.owner_role || '',
        Initiative: this.humanize(g.initiative_key),
        Gap: this.humanize(g.gap_type),
        Constraint: g.title,
        Status: this.humanize(g.status),
        Severity: g.severity || '',
        Raised: g.raised_at || '',
        Resolved: g.closed_at || '',
        Actions: g.actions.length,
        'Actions Done': g.actions_done,
      }));
    },
    exportXlsx() {
      const rows = this.exportRows();
      if (!rows.length) return;
      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `performance_signals_${today}.xlsx`, { sheetName: 'Constraints' });
    },
    exportCsv() {
      const rows = this.exportRows();
      if (!rows.length) return;
      if (blockSensitiveEgress('export')) return;
      const cols = Object.keys(rows[0]);
      const esc = v => {
        const str = v == null ? '' : String(v);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
      const today = new Date().toISOString().split('T')[0];
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance_signals_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/constraints/');
        this.rows = (data.constraints || []).map(g => ({
          ...g,
          actions_count: g.actions.length,
          actions_done: g.actions.filter(a => a.status === 'resolved').length,
        }));
      } catch (err) {
        console.error('Error loading performance signals:', err);
        this.error = 'Failed to load';
      } finally {
        this.loading = false;
      }
    },
    async fetchImpact() {
      this.impactLoading = true;
      try {
        const { data } = await axios.get('api/engagement-graph-nodes/action-metric-impact/');
        this.impact = data.initiatives || [];
      } catch (err) {
        console.error('Error loading action impact:', err);
        this.impact = [];
      } finally {
        this.impactLoading = false;
      }
    },
  },
};
</script>
